import { FC, useState, DragEvent } from 'react'
import { useGetList, useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import SearchIcon from '@mui/icons-material/Search'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ScheduleIcon from '@mui/icons-material/Schedule'
// TODO: re-enable when webhook trigger is wired up on the backend (endpoint + auth).
// import WebhookIcon from '@mui/icons-material/Webhook'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import BuildIcon from '@mui/icons-material/Build'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import LoopIcon from '@mui/icons-material/Loop'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import TableRowsIcon from '@mui/icons-material/TableRows'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import HubIcon from '@mui/icons-material/Hub'
import PsychologyIcon from '@mui/icons-material/Psychology'
import TableChartIcon from '@mui/icons-material/TableChart'
import StorageIcon from '@mui/icons-material/Storage'
import AltRouteIcon from '@mui/icons-material/AltRoute'
import { themeColor } from '../../layout/theme/color'
import { WORKFLOW } from './tokens'
import { SidebarDragData, WorkflowNodeType } from './types'
import { McpServerOut } from '../../types/apiRequests'

interface SidebarItemProps {
  icon: React.ReactNode
  label: string
  description: string
  dragData: SidebarDragData
  accentColor: string
}

const SidebarItem: FC<SidebarItemProps> = ({ icon, label, description, dragData, accentColor }) => {
  const handleDragStart = (event: DragEvent) => {
    event.dataTransfer.setData('application/workflow-node', JSON.stringify(dragData))
    event.dataTransfer.effectAllowed = 'move'
  }

  return (
    <Box
      draggable
      onDragStart={handleDragStart}
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'gap': 1,
        'pl': 0.5,
        'pr': 1.5,
        'py': 0.75,
        'borderRadius': '10px',
        'cursor': 'grab',
        'border': '1px solid transparent',
        'transition': 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          background: themeColor.slate05,
          borderColor: themeColor.slate10,
          transform: 'translateX(2px)',
        },
        '&:hover .grip-handle': {
          opacity: 0.5,
        },
        '&:active': {
          cursor: 'grabbing',
          transform: 'scale(0.97)',
        },
      }}
    >
      <DragIndicatorIcon
        className="grip-handle"
        sx={{
          fontSize: 14,
          color: themeColor.slate40,
          opacity: 0.15,
          transition: 'opacity 0.2s ease',
          flexShrink: 0,
        }}
      />
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          background: `${accentColor}10`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accentColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13, lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate50, fontSize: 11, lineHeight: 1.2, display: 'block' }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

interface SidebarSectionProps {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}

const SidebarSection: FC<SidebarSectionProps> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          'display': 'flex',
          'alignItems': 'center',
          'justifyContent': 'space-between',
          'px': 1.5,
          'py': 0.5,
          'cursor': 'pointer',
          'borderRadius': '8px',
          '&:hover': { background: themeColor.slate05 },
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontSize: 10, fontWeight: 700, color: themeColor.slate40, letterSpacing: 1 }}
        >
          {title}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  )
}

const WorkflowSidebar: FC = () => {
  const translate = useTranslate()
  const [search, setSearch] = useState('')

  const { data: mcpServers, isLoading } = useGetList<McpServerOut>('mcp_server', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'display_name', order: 'ASC' },
    filter: { enabled: true },
  })

  const matchesSearch = (text: string): boolean =>
    !search || text.toLowerCase().includes(search.toLowerCase())

  return (
    <Box
      sx={{
        width: WORKFLOW.sidebar.width,
        borderRight: `1px solid ${themeColor.slate10}`,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${themeColor.slate10}` }}>
        <TextField
          size="small"
          fullWidth
          placeholder={translate('workflow.sidebar.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: themeColor.slate40 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              'borderRadius': '10px',
              'fontSize': 13,
              '& fieldset': { borderColor: themeColor.slate10 },
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {matchesSearch('trigger cron manual mcp') && (
          <SidebarSection title={translate('workflow.sidebar.triggers')}>
            {matchesSearch('cron schedule') && (
              <SidebarItem
                icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.cron')}
                description={translate('workflow.nodes.cronDesc')}
                dragData={{ nodeType: 'trigger', triggerType: 'cron' }}
                accentColor={WORKFLOW.trigger.accent}
              />
            )}
            {/* TODO: re-enable when webhook trigger is wired up on the backend (endpoint + auth).
            {matchesSearch('webhook') && (
              <SidebarItem
                icon={<WebhookIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.webhook')}
                description={translate('workflow.nodes.webhookDesc')}
                dragData={{ nodeType: 'trigger', triggerType: 'webhook' }}
                accentColor={WORKFLOW.trigger.accent}
              />
            )}
            */}
            {matchesSearch('manual') && (
              <SidebarItem
                icon={<PlayArrowIcon sx={{ fontSize: 20 }} />}
                label={translate('workflow.nodes.manual')}
                description={translate('workflow.nodes.manualDesc')}
                dragData={{ nodeType: 'trigger', triggerType: 'manual' }}
                accentColor={WORKFLOW.trigger.accent}
              />
            )}
            {matchesSearch('mcp tool ai claude') && (
              <SidebarItem
                icon={<HubIcon sx={{ fontSize: 16 }} />}
                label={translate('workflow.nodes.mcpTrigger')}
                description={translate('workflow.nodes.mcpTriggerDesc')}
                dragData={{ nodeType: 'trigger', triggerType: 'mcp' }}
                accentColor={WORKFLOW.trigger.accent}
              />
            )}
          </SidebarSection>
        )}

        <SidebarSection title={translate('workflow.sidebar.mcpTools')}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {mcpServers
            ?.filter((s) => matchesSearch(`${s.display_name} ${s.name}`))
            .map((server) => (
              <SidebarItem
                key={server.id}
                icon={<BuildIcon sx={{ fontSize: 18 }} />}
                label={server.display_name}
                description={server.name}
                dragData={{
                  nodeType: 'tool_call' as WorkflowNodeType,
                  serverId: server.id,
                  serverName: server.name,
                }}
                accentColor={WORKFLOW.tool_call.accent}
              />
            ))}
        </SidebarSection>

        {matchesSearch('ai llm model gpt claude') && (
          <SidebarSection title={translate('workflow.sidebar.ai')}>
            <SidebarItem
              icon={<PsychologyIcon sx={{ fontSize: 18 }} />}
              label={translate('workflow.nodes.aiCall')}
              description={translate('workflow.nodes.aiCallDesc')}
              dragData={{ nodeType: 'ai_call' as WorkflowNodeType }}
              accentColor={WORKFLOW.ai_call.accent}
            />
          </SidebarSection>
        )}

        {matchesSearch('condition loop merge logic') && (
          <SidebarSection title={translate('workflow.sidebar.logic')}>
            {matchesSearch('condition if else') && (
              <SidebarItem
                icon={<CallSplitIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.condition')}
                description={translate('workflow.nodes.conditionDesc')}
                dragData={{ nodeType: 'condition' }}
                accentColor={WORKFLOW.condition.accent}
              />
            )}
            {matchesSearch('loop iterate') && (
              <SidebarItem
                icon={<LoopIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.loop')}
                description={translate('workflow.nodes.loopDesc')}
                dragData={{ nodeType: 'loop' }}
                accentColor={WORKFLOW.loop.accent}
              />
            )}
            {matchesSearch('merge wait') && (
              <SidebarItem
                icon={<MergeTypeIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.merge')}
                description={translate('workflow.nodes.mergeDesc')}
                dragData={{ nodeType: 'merge' }}
                accentColor={WORKFLOW.merge.accent}
              />
            )}
          </SidebarSection>
        )}

        {matchesSearch(
          'transform data mapping visualization table file attachment xml json parse parser '
          + 'inavigator field data store redis vector embedding semantic',
        ) && (
          <SidebarSection title={translate('workflow.sidebar.data')}>
            <SidebarItem
              icon={<SwapHorizIcon sx={{ fontSize: 18 }} />}
              label={translate('workflow.nodes.transform')}
              description={translate('workflow.nodes.transformDesc')}
              dragData={{ nodeType: 'transform' }}
              accentColor={WORKFLOW.transform.accent}
            />
            {matchesSearch('field mapping fields rename transform uppercase lookup table') && (
              <SidebarItem
                icon={<TableRowsIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.fieldMapping')}
                description={translate('workflow.nodes.fieldMappingDesc')}
                // eslint-disable-next-line camelcase
                dragData={{ nodeType: 'field_mapping' }}
                accentColor={WORKFLOW.fieldMapping.accent}
              />
            )}
            {matchesSearch('data file attachment xml json source') && (
              <SidebarItem
                icon={<StorageIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.data')}
                description={translate('workflow.nodes.dataDesc')}
                dragData={{ nodeType: 'data' }}
                accentColor={WORKFLOW.data.accent}
              />
            )}
            {matchesSearch('parse parser inavigator zaaktype payload') && (
              <SidebarItem
                icon={<AltRouteIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.parse')}
                description={translate('workflow.nodes.parseDesc')}
                dragData={{ nodeType: 'parse' }}
                accentColor={WORKFLOW.parse.accent}
              />
            )}
            {matchesSearch('visualization table chart') && (
              <SidebarItem
                icon={<TableChartIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.visualization')}
                description={translate('workflow.nodes.visualizationDesc')}
                dragData={{ nodeType: 'visualization' }}
                accentColor={WORKFLOW.visualization.accent}
              />
            )}
            {matchesSearch(
              'data store write read update delete redis vector embedding search lookup remove',
            ) && (
              <SidebarItem
                icon={<StorageIcon sx={{ fontSize: 18 }} />}
                label={translate('workflow.nodes.dataStore')}
                description={translate('workflow.nodes.dataStoreDesc')}
                // eslint-disable-next-line camelcase
                dragData={{ nodeType: 'data_store' }}
                accentColor={WORKFLOW.data_store.accent}
              />
            )}
          </SidebarSection>
        )}
      </Box>
    </Box>
  )
}

export default WorkflowSidebar
